import ExcelJS from "exceljs";
import { csvDecimalParts, type CsvColumn } from "@hisense/shared";
import { GREY, INK, THIN_BORDER, downloadWorkbook } from "./excelSheet";

/**
 * Xuất một bảng danh sách ra .xlsx — CÙNG bộ cột với bản CSV, chỉ khác vỏ tệp.
 *
 * Vì sao có thêm .xlsx (16/09/2026, chủ dự án yêu cầu "xuất dữ liệu phải có
 * Excel"): người dùng mở tệp bằng Excel là chính, và CSV đến tay họ theo cách
 * Excel đoán — dấu phân cách theo vùng, số chứng từ "000413" bị cắt số 0, cột
 * tiền mất định dạng. Tệp .xlsx ghi đúng kiểu từng ô nên không còn phải đoán.
 * exceljs vốn đã nằm trong gói giao diện cho PI / báo giá / hoá đơn, nên không
 * tốn thêm dung lượng tải trang.
 *
 * Ô SỐ LÀ SỐ: tiền trên các trang danh sách đi qua `majorUnits` thành chuỗi
 * "1234.50" để CSV in được. Trong .xlsx mà ghi chuỗi thì Excel gắn tam giác xanh
 * "số lưu dạng chữ" và SUM bỏ qua ô đó — trái với lời hứa "tiền là số trần để
 * công thức chạy được". Nên chuỗi thập phân thuần (có dấu chấm) được ghi thành
 * số, giữ đúng số lẻ bằng định dạng ô. Chuỗi TOÀN CHỮ SỐ không đổi: đó là số
 * chứng từ, mã sản phẩm, số điện thoại — "000413" phải còn nguyên ba số 0.
 *
 * Không có chuyện CSV injection ở đây: .xlsx ghi kiểu ô riêng, chuỗi bắt đầu
 * bằng `=` vẫn là chuỗi, Excel không suy công thức từ nội dung như khi mở CSV.
 */

/** Tên sheet Excel: tối đa 31 ký tự, không được chứa : \ / ? * [ ] */
export function sheetName(title: string): string {
  const clean = title.replace(/[:\\/?*[\]]/g, " ").replace(/\s+/g, " ").trim().slice(0, 31).trim();
  return clean || "Data";
}

type CellValue = string | number | boolean | Date | null;

/** Giá trị ô và định dạng số (nếu là số có số lẻ cố định). */
export function xlsxCell(raw: unknown): { value: CellValue; numFmt?: string } {
  if (raw === null || raw === undefined) return { value: null };
  const decimal = csvDecimalParts(raw);
  if (decimal) return { value: decimal.value, numFmt: numFmt(decimal.dp) };
  if (typeof raw === "number") return Number.isFinite(raw) ? { value: raw } : { value: String(raw) };
  if (typeof raw === "boolean" || raw instanceof Date) return { value: raw };
  const s = String(raw);
  const fixed = /^-?\d+\.(\d+)$/.exec(s);
  if (fixed) return { value: Number(s), numFmt: numFmt(fixed[1]!.length) };
  return { value: s };
}

const numFmt = (dp: number) => (dp > 0 ? `0.${"0".repeat(dp)}` : "0");

/** Độ rộng hiển thị gần đúng: ký tự CJK rộng gần gấp đôi chữ Latin. */
const displayWidth = (s: string) =>
  Array.from(s).reduce((k, ch) => k + (ch.charCodeAt(0) > 255 ? 2 : 1), 0);

/** Chỉ đo độ rộng cột trên những dòng đầu — đủ đúng, và không đi hết 50 000 dòng chỉ để chọn bề rộng. */
const WIDTH_SAMPLE_ROWS = 500;

/**
 * Ghi một bảng (dòng tiêu đề + các dòng giá trị thô) vào sheet từ `startRow`.
 *
 * Dòng tiêu đề được đông cứng và mang bộ lọc — hai thứ đầu tiên ai mở một bảng
 * dài trong Excel cũng tự tay làm. `startRow` > 1 dành cho sheet có khối
 * thông tin phía trên (bảng kê công nợ ghi kỳ, phạm vi, múi giờ trước bảng).
 */
export function writeTable(
  sheet: ExcelJS.Worksheet,
  header: readonly string[],
  rows: readonly (readonly unknown[])[],
  startRow = 1,
): void {
  const widths = header.map((h, i) => Math.max(sheet.getColumn(i + 1).width ?? 0, displayWidth(h) + 2));

  const head = sheet.getRow(startRow);
  head.values = [...header];
  head.eachCell((cell) => {
    cell.font = { name: "Arial", size: 10, bold: true, color: { argb: INK } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: GREY } };
    cell.border = THIN_BORDER;
    cell.alignment = { vertical: "middle", wrapText: true };
  });

  rows.forEach((values, index) => {
    const cells = values.map(xlsxCell);
    const line = sheet.getRow(startRow + 1 + index);
    line.values = cells.map((c) => c.value);
    cells.forEach((c, i) => {
      if (c.numFmt) line.getCell(i + 1).numFmt = c.numFmt;
      if (index < WIDTH_SAMPLE_ROWS && c.value !== null) {
        widths[i] = Math.max(widths[i]!, displayWidth(c.value instanceof Date ? c.value.toISOString() : String(c.value)) + 2);
      }
    });
  });

  widths.forEach((w, i) => {
    sheet.getColumn(i + 1).width = Math.min(60, Math.max(8, w));
  });
  sheet.views = [{ state: "frozen", ySplit: startRow }];
  sheet.autoFilter = { from: { row: startRow, column: 1 }, to: { row: startRow, column: header.length } };
}

export function newWorkbook(): ExcelJS.Workbook {
  const book = new ExcelJS.Workbook();
  book.creator = "Hisense Button ERP";
  book.created = new Date();
  return book;
}

/** Phần THUẦN, kiểm thử được trong Node: dựng workbook một sheet, không đụng DOM. */
export function buildTableWorkbook<T>(
  title: string,
  columns: readonly CsvColumn<T>[],
  rows: readonly T[],
): ExcelJS.Workbook {
  const book = newWorkbook();
  const sheet = book.addWorksheet(sheetName(title));
  writeTable(
    sheet,
    columns.map((c) => c.header),
    rows.map((row) => columns.map((c) => c.value(row))),
  );
  return book;
}

export async function exportXlsx<T>(
  filename: string,
  title: string,
  columns: readonly CsvColumn<T>[],
  rows: readonly T[],
): Promise<void> {
  await downloadWorkbook(buildTableWorkbook(title, columns, rows), filename);
}
